// src/app/core/service/auth-management.service.ts
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Router } from '@angular/router';
import { environment } from '@enviroment/environment';
import { decryptData } from '@shared/utils/decrypt';
import { UserRole } from '@core/models/Enums/UserRole';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import Swal from 'sweetalert2';

type JwtClaims = {
  role?: string;
  permissions?: string[];
  nbf?: number;
  exp?: number;
  [k: string]: any;
};

@Injectable({ providedIn: 'root' })
export class AuthManagementService {
  private readonly IS_TUTOR_KEY = 'isTutor';
  private expiredSessionLogoutInProgress = false;

  constructor(
    private jwtHelper: JwtHelperService,
    private http: HttpClient,
    private router: Router
  ) {}

  public saveUserKey(token: string): void {
    sessionStorage.setItem(environment.cypher_key, token);
    localStorage.setItem(environment.cypher_key, token);
  }

  public saveUserData(data: string): void {
    sessionStorage.setItem(environment.user_data, data);
    localStorage.setItem(environment.user_data, data);
  }

  public getUserKey(): string | null {
    return (
      localStorage.getItem(environment.cypher_key) ||
      sessionStorage.getItem(environment.cypher_key)
    );
  }

  public getUserData(): string | null {
    return (
      localStorage.getItem(environment.user_data) ||
      sessionStorage.getItem(environment.user_data)
    );
  }

  /**
   * Desencripta el token almacenado.
   */
  public getToken(): string | null {
    try {
      const enc = this.getUserKey();
      if (!enc) return null;
      return decryptData(enc, environment.encryptionKey);
    } catch {
      return null;
    }
  }

  // ========= TUTOR FLAG =========

  /**
   * Guarda la bandera de tutor en sessionStorage.
   */
  public saveIsTutor(isTutor: boolean): void {
    sessionStorage.setItem(this.IS_TUTOR_KEY, isTutor ? '1' : '0');
    localStorage.removeItem(this.IS_TUTOR_KEY);
  }

  /**
   * Lee la bandera de tutor con fallback legado.
   */
  public getIsTutor(): boolean {
    return (
      sessionStorage.getItem(this.IS_TUTOR_KEY) === '1' ||
      localStorage.getItem(this.IS_TUTOR_KEY) === '1'
    );
  }

  // ========= JWT helpers =========

  private decodeToken(): JwtClaims | null {
    const token = this.getToken();
    if (!token) return null;

    try {
      return this.jwtHelper.decodeToken(token) as JwtClaims;
    } catch {
      return null;
    }
  }

  public isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) return false;

    const SKEW_SECONDS = 300;

    const expDate = this.jwtHelper.getTokenExpirationDate(token);
    if (!expDate) return false;
    if (Date.now() >= expDate.getTime()) return false;

    const claims = this.decodeToken();
    const nbf = claims?.nbf ?? 0;
    const nowWithSkew = Math.floor(Date.now() / 1000) + SKEW_SECONDS;

    if (nbf && nowWithSkew < nbf) return false;

    return true;
  }

  // ========= Roles & Permisos desde el JWT =========

  public getRole(): UserRole | null {
    const roleRaw = (this.decodeToken()?.role || '').toString().trim();
    if (!roleRaw) return null;

    const match = (Object.values(UserRole) as string[])
      .find(v => v.toLowerCase() === roleRaw.toLowerCase());

    return (match as UserRole) ?? (roleRaw as UserRole);
  }

  public hasRole(roles: UserRole | UserRole[]): boolean {
    const current = this.getRole();
    if (!current) return false;

    const arr = Array.isArray(roles) ? roles : [roles];

    return arr.some(
      r => r.toLowerCase() === (current as unknown as string).toLowerCase()
    );
  }

  public getPermissions(): string[] {
    const p = this.decodeToken()?.permissions;
    return Array.isArray(p) ? p : [];
  }

  public hasPermission(perm: string | string[]): boolean {
    const needed = Array.isArray(perm) ? perm : [perm];
    const mine = this.getPermissions();

    if (!mine.length && needed.length) return false;

    return needed.every(n => mine.includes(n));
  }

  // ========= Datos de usuario para UI =========

  public userData(): any {
    const raw = this.getUserData();
    if (!raw) return null;

    try {
      return JSON.parse(decryptData(raw, environment.encryptionKey));
    } catch {
      return null;
    }
  }

  // ========= Helpers legibles =========

  public isUserAdmin(): boolean {
    return this.hasRole(UserRole.Admin);
  }

  public isUserDoctor(): boolean {
    return this.hasRole(UserRole.Medico);
  }

  public isUserPatient(): boolean {
    return this.hasRole(UserRole.Paciente);
  }

  public isUserParticular(): boolean {
    return this.hasRole(UserRole.Particular);
  }

  public isUserPharmacySupervision(): boolean {
    return this.hasRole(UserRole.Supervisor_Sucursales);
  }

  public isUserPharmacyResponsible(): boolean {
    return this.hasRole(UserRole.Responsable_Farmacia);
  }

  public isUserPharmacyEmployee(): boolean {
    return this.hasRole(UserRole.Empleado_Farmacia);
  }

  // ========= Logout =========

  public logout$(): Observable<boolean> {
    const token = this.getToken();
    const url = `${environment.backend_url}Auth/logout`;

    const headers = token
      ? new HttpHeaders({ Authorization: `Bearer ${token}` })
      : new HttpHeaders();

    return this.http.post<any>(url, {}, { headers }).pipe(
      map(() => true),
      catchError(() => of(false)),
      finalize(() => {
        this.hardLocalLogout();
        this.router.navigateByUrl('/authentication/signin', { replaceUrl: true });
      })
    );
  }

  public hardLocalLogout(): void {
    // localStorage
    localStorage.removeItem(environment.cypher_key);
    localStorage.removeItem(environment.user_data);
    localStorage.removeItem('branchGroupId');
    localStorage.removeItem('businessGroupId');
    localStorage.removeItem(this.IS_TUTOR_KEY);
    localStorage.removeItem('rmd_2fa_ok_until');

    // sessionStorage
    sessionStorage.removeItem(environment.cypher_key);
    sessionStorage.removeItem(environment.user_data);
    sessionStorage.removeItem('branchGroupId');
    sessionStorage.removeItem('businessGroupId');
    sessionStorage.removeItem(this.IS_TUTOR_KEY);
  }

  public logout(): void {
    this.hardLocalLogout();
    this.router.navigateByUrl('/authentication/signin', { replaceUrl: true });
  }

  public isExpiredSessionLogoutInProgress(): boolean {
    return this.expiredSessionLogoutInProgress;
  }

  public logoutDueToExpiredSession(): void {
    if (this.expiredSessionLogoutInProgress) {
      return;
    }

    this.expiredSessionLogoutInProgress = true;

    void Swal.fire({
      title: 'Sesion expirada',
      html: 'Tu sesion se cerrara porque el token ya expiro.<br>Redirigiendo al inicio de sesion...',
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    window.setTimeout(() => {
      this.hardLocalLogout();
      this.router.navigateByUrl('/authentication/signin', { replaceUrl: true })
        .finally(() => {
          window.setTimeout(() => {
            Swal.close();
            this.expiredSessionLogoutInProgress = false;
          }, 600);
        });
    }, 900);
  }

  public getDefaultRoute(): string {
    const currentRegion = String(environment.region ?? '').trim().toUpperCase();

    if (this.isUserPharmacyEmployee() || this.isUserPharmacyResponsible()) {
      return '/prescription/prescription-supply';
    }

    if (this.isUserDoctor() || this.isUserParticular()) {
      return '/patient/patient-list';
    }

    if (this.isUserPatient()) {
      return '/dashboard';
    }

    if (this.isUserAdmin() || this.isUserPharmacySupervision()) {
      if (currentRegion === 'ES') {
        return '/doctor/doctor-list';
      }

      return '/admin/user-list';
    }

    return '/dashboard';
  }
}
